import { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { PageHeader, Surface, Badge, Button, Modal, Input, Select, NewButton } from "@/components/ui-kit";
import VehicleSearchSelect from "@/components/VehicleSearchSelect";
import { UploadSimple, FileText, Trash, Sparkle, MagnifyingGlass, DownloadSimple, Printer, X, CheckCircle } from "@phosphor-icons/react";
import { formatDate, daysUntil } from "@/lib/format";
import { downloadCsv, printElement, regSort } from "@/lib/csv";

const empty = { name: "", phone: "", email: "", license_number: "", license_class: "LMV", license_expiry: "", cov_classes: [], rto_authority: "", hazardous_license: false, hazardous_license_expiry: "", vehicle_id: "", status: "active", notes: "" };

const CLASSES = ["LMV", "LMV-NT", "MGV", "HMV", "HTV", "MCWG", "MCWOG"];
const STATUSES = ["active", "on_leave", "suspended"];

// Each COV row needs a stable identity so React reconciliation doesn't reuse
// state across rows when one is removed. We tag freshly-added or freshly-loaded
// rows with `_uid` so the table can key on it instead of array index.
let _covUidCounter = 0;
const _newCovUid = () => `cov-${Date.now()}-${++_covUidCounter}`;
const withCovUids = (rows) =>
    (Array.isArray(rows) ? rows : []).map((r) => (r && r._uid ? r : { ...r, _uid: _newCovUid() }));

export default function Drivers() {
    const [list, setList] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);
    const [docs, setDocs] = useState([]); // license docs for the driver currently being edited
    const [docsFor, setDocsFor] = useState(null); // driver whose docs are being viewed from the row
    const [rowDocs, setRowDocs] = useState([]);

    // AI / Manual mode for the Add Driver flow
    const [addMode, setAddMode] = useState("ai"); // "ai" | "manual"
    const [aiFiles, setAiFiles] = useState([]);
    const [aiExtracted, setAiExtracted] = useState(null);
    const [aiBusy, setAiBusy] = useState(false);
    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterClass, setFilterClass] = useState("");
    const [filterExpiry, setFilterExpiry] = useState(""); // expired | 30 | 60 | 90
    const tableRef = useRef(null);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return list.filter((d) => {
            if (q && !((d.name || "").toLowerCase().includes(q) || (d.license_number || "").toLowerCase().includes(q))) return false;
            if (filterStatus && d.status !== filterStatus) return false;
            if (filterClass && d.license_class !== filterClass) return false;
            if (filterExpiry) {
                const days = d.license_expiry ? daysUntil(d.license_expiry) : null;
                if (days === null) return false;
                if (filterExpiry === "expired") {
                    if (days >= 0) return false;
                } else {
                    const limit = parseInt(filterExpiry, 10);
                    if (days < 0 || days > limit) return false;
                }
            }
            return true;
        });
    }, [list, search, filterStatus, filterClass, filterExpiry]);

    const clearFilters = () => { setSearch(""); setFilterStatus(""); setFilterClass(""); setFilterExpiry(""); };
    const hasFilters = search || filterStatus || filterClass || filterExpiry;

    const load = () => api.get("/drivers").then((r) => setList(r.data));
    useEffect(() => {
        load();
        api.get("/vehicles").then((r) => setVehicles(r.data));
    }, []);

    // Manual mode: license pages to attach to a brand-new driver (uploaded after save)
    const [manualFiles, setManualFiles] = useState([]);

    const save = async () => {
        try {
            // Strip client-only `_uid` from cov_classes before persisting
            const cleanCov = (form.cov_classes || []).map(({ _uid, ...rest }) => rest);
            const payload = { ...form, cov_classes: cleanCov, vehicle_id: form.vehicle_id || null };
            let driverId = editing;
            if (editing) await api.put(`/drivers/${editing}`, payload);
            else {
                const r = await api.post("/drivers", payload);
                driverId = r.data?.id;
            }
            // After-save: in AI mode upload the scans the AI used; in Manual mode upload the manually-attached pages.
            const filesToUpload = !editing
                ? (addMode === "ai" ? aiFiles : manualFiles)
                : [];
            if (driverId && filesToUpload.length > 0) {
                let okCount = 0;
                for (const f of filesToUpload) {
                    const fd = new FormData(); fd.append("file", f);
                    try {
                        await api.post(
                            `/upload?entity_type=driver&entity_id=${driverId}&label=${encodeURIComponent("Driving Licence")}`,
                            fd, { headers: { "Content-Type": "multipart/form-data" } },
                        );
                        okCount += 1;
                    } catch (err) {
                        console.warn("Driver licence upload failed for one file:", err);
                    }
                }
                if (okCount > 0) toast.success(`Saved · ${okCount} licence page(s) attached`);
                else toast.success("Saved");
            } else {
                toast.success("Saved");
            }
            setOpen(false); setEditing(null); setForm(empty);
            setAddMode("ai"); setAiFiles([]); setAiExtracted(null); setManualFiles([]);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const runAiExtract = async () => {
        if (aiFiles.length === 0) { toast.error("Upload at least one image / PDF of the licence"); return; }
        setAiBusy(true);
        const tid = toast.loading(`Extracting fields from ${aiFiles.length} page(s) with AI…`);
        try {
            const fd = new FormData();
            for (const f of aiFiles) fd.append("files", f);
            const r = await api.post("/drivers/extract-license", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 90000,
            });
            const x = r.data.extracted || {};
            // Pick the latest non-empty valid_to from cov_classes as a fallback expiry
            const covList = withCovUids(
                Array.isArray(x.cov_classes) ? x.cov_classes.filter((c) => c && c.class_code) : []
            );
            const expiryFallback = covList.map((c) => c.valid_to).filter(Boolean).sort().pop();
            // Map AI fields into the form. Keep manual edits the user has typed.
            setForm((prev) => ({
                ...prev,
                name: prev.name || x.name || "",
                license_number: prev.license_number || (x.license_number || "").toUpperCase(),
                license_class: x.license_class && CLASSES.includes(x.license_class) ? x.license_class : prev.license_class,
                license_expiry: x.license_expiry || expiryFallback || prev.license_expiry,
                cov_classes: covList.length > 0 ? covList : prev.cov_classes,
                rto_authority: x.rto_authority || prev.rto_authority,
                hazardous_license: typeof x.hazardous_license === "boolean" ? x.hazardous_license : prev.hazardous_license,
                hazardous_license_expiry: x.hazardous_license_expiry || prev.hazardous_license_expiry,
                status: x.status_hint && ["active", "on_leave", "suspended"].includes(x.status_hint) ? x.status_hint : prev.status,
            }));
            setAiExtracted({ ...x, cov_classes: covList });
            if (x._name_warning) toast.warning(x._name_warning, { duration: 8000 });
            toast.success("AI extraction complete — please verify the fields", { id: tid });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "AI extraction failed", { id: tid });
        } finally {
            setAiBusy(false);
        }
    };

    const edit = async (d) => {
        setForm({ ...empty, ...d, vehicle_id: d.vehicle_id || "", license_expiry: d.license_expiry || "", hazardous_license: !!d.hazardous_license, hazardous_license_expiry: d.hazardous_license_expiry || "", cov_classes: withCovUids(d.cov_classes) });
        setEditing(d.id);
        setManualFiles([]);
        setOpen(true);
        try {
            const r = await api.get(`/drivers/${d.id}/documents`);
            setDocs(r.data || []);
        } catch (e) { setDocs([]); }
    };
    const del = async (id) => { if (!window.confirm("Delete driver?")) return; await api.delete(`/drivers/${id}`); load(); };

    // Document operations from within the Edit modal
    const reloadDocs = async () => {
        if (!editing) return;
        const r = await api.get(`/drivers/${editing}/documents`);
        setDocs(r.data || []);
    };
    const uploadLicense = async (file) => {
        if (!editing) return;
        const fd = new FormData(); fd.append("file", file);
        const tid = toast.loading("Uploading & extracting fields with AI…");
        try {
            const r = await api.post(`/compliance-documents?doc_type=driving_license&driver_id=${editing}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(r.data.ai_status === "ok" ? "License processed by AI" : "Uploaded (AI partial)", { id: tid });
            reloadDocs(); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail), { id: tid }); }
    };
    const deleteDoc = async (doc) => {
        if (!window.confirm("Delete this document?")) return;
        try {
            if (doc.kind === "compliance") {
                await api.delete(`/compliance-documents/${doc.id}`);
            } else {
                await api.delete(`/files/${doc.file_id}`);
            }
            reloadDocs(); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    // Row-level "Docs" quick-view (independent of Edit modal)
    const openRowDocs = async (driver) => {
        setDocsFor(driver);
        try {
            const r = await api.get(`/drivers/${driver.id}/documents`);
            setRowDocs(r.data || []);
        } catch (e) { setRowDocs([]); }
    };
    const reloadRowDocs = async () => {
        if (!docsFor) return;
        const r = await api.get(`/drivers/${docsFor.id}/documents`);
        setRowDocs(r.data || []);
    };
    const uploadRowDoc = async (file) => {
        if (!docsFor) return;
        const fd = new FormData(); fd.append("file", file);
        const tid = toast.loading("Uploading & extracting fields with AI…");
        try {
            const r = await api.post(`/compliance-documents?doc_type=driving_license&driver_id=${docsFor.id}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(r.data.ai_status === "ok" ? "License processed by AI" : "Uploaded (AI partial)", { id: tid });
            reloadRowDocs(); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail), { id: tid }); }
    };
    const deleteRowDoc = async (doc) => {
        if (!window.confirm("Delete this document?")) return;
        try {
            if (doc.kind === "compliance") {
                await api.delete(`/compliance-documents/${doc.id}`);
            } else {
                await api.delete(`/files/${doc.file_id}`);
            }
            reloadRowDocs(); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const exportCsv = () => {
        downloadCsv(`drivers-${new Date().toISOString().slice(0, 10)}.csv`, filtered, [
            ["NAME", "name"], ["PHONE", "phone"], ["EMAIL", "email"],
            ["DL #", "license_number"],
            ["CLASS · EXPIRY", (d) => {
                const list = Array.isArray(d.cov_classes) && d.cov_classes.length > 0
                    ? d.cov_classes
                    : (d.license_class ? [{ class_code: d.license_class, valid_to: d.license_expiry }] : []);
                return list.map((c) => `${c.class_code}${c.valid_to ? " (" + formatDate(c.valid_to) + ")" : ""}`).join(" | ");
            }],
            ["HAZARDOUS LICENSE", (d) => (d.hazardous_license ? "YES" : "NO")],
            ["HAZARDOUS EXPIRY", (d) => formatDate(d.hazardous_license_expiry)],
            ["VEHICLE", (d) => vehMap[d.vehicle_id]?.reg_number || ""],
            ["STATUS", "status"], ["NOTES", "notes"],
        ]);
    };
    const exportPdf = () => printElement(tableRef.current, `EAZYFLEET · Drivers · ${filtered.length} record(s)`);

    const vehMap = Object.fromEntries(vehicles.map((v) => [v.id, v]));

    return (
        <div className="p-6 lg:p-8 animate-fade-in">
            <PageHeader
                eyebrow="PEOPLE"
                title="Drivers"
                subtitle={`${list.length} drivers · license tracking`}
                action={<NewButton onClick={() => { setForm(empty); setEditing(null); setAddMode("ai"); setAiFiles([]); setAiExtracted(null); setManualFiles([]); setOpen(true); }} label="Add driver" testid="add-driver-btn" />}
            />

            <Surface className="overflow-hidden">
                <div className="px-5 py-3 border-b border-fleet-border flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[260px] max-w-md">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fleet-inkMuted" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name or DL number…"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink"
                            data-testid="drivers-search-input"
                        />
                    </div>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                        data-testid="drivers-filter-status">
                        <option value="">All statuses</option>
                        {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                    <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)}
                        className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                        data-testid="drivers-filter-class">
                        <option value="">All classes</option>
                        {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={filterExpiry} onChange={(e) => setFilterExpiry(e.target.value)}
                        className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                        data-testid="drivers-filter-expiry">
                        <option value="">Any expiry</option>
                        <option value="expired">Expired</option>
                        <option value="30">DL due ≤ 30 days</option>
                        <option value="60">DL due ≤ 60 days</option>
                        <option value="90">DL due ≤ 90 days</option>
                    </select>
                    {hasFilters && (
                        <button onClick={clearFilters} className="text-xs text-fleet-inkSoft hover:text-fleet-ink underline flex items-center gap-1" data-testid="drivers-clear-filters">
                            <X size={12} /> Clear
                        </button>
                    )}
                    <span className="text-xs text-fleet-inkSoft ml-auto">{filtered.length} of {list.length}</span>
                    <Button variant="secondary" size="sm" onClick={exportCsv} data-testid="drivers-export-csv">
                        <DownloadSimple size={14} /> CSV
                    </Button>
                    <Button variant="secondary" size="sm" onClick={exportPdf} data-testid="drivers-export-pdf">
                        <Printer size={14} /> PDF
                    </Button>
                </div>
                <table className="w-full text-sm" ref={tableRef}>
                    <thead className="bg-slate-50"><tr className="text-left">
                        {["NAME", "PHONE", "DL #", "CLASS · EXPIRY", "HAZMAT EXPIRY", "VEHICLE", "STATUS", ""].map((h, i) => <th key={h} className={`label-mono px-5 py-3 font-normal${i === 7 ? " no-print" : ""}`}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {filtered.map((d) => {
                            const covList = Array.isArray(d.cov_classes) && d.cov_classes.length > 0
                                ? d.cov_classes
                                : (d.license_class ? [{ class_code: d.license_class, valid_to: d.license_expiry }] : []);
                            return (
                                <tr key={d.id} className="border-t border-fleet-border data-grid-row">
                                    <td className="px-5 py-3 font-medium">{d.name}</td>
                                    <td className="px-5 py-3 font-mono">{d.phone || "—"}</td>
                                    <td className="px-5 py-3 font-mono text-xs">{d.license_number || "—"}</td>
                                    <td className="px-5 py-3">
                                        {covList.length === 0 ? <span className="text-fleet-inkSoft">—</span> : (
                                            <div className="flex flex-wrap gap-1">
                                                {covList.map((c, i) => {
                                                    const days = c.valid_to ? daysUntil(c.valid_to) : null;
                                                    const tone = days == null
                                                        ? "bg-slate-100 text-fleet-inkSoft"
                                                        : days < 0 ? "bg-red-50 text-fleet-critical"
                                                            : days <= 30 ? "bg-amber-50 text-fleet-warning"
                                                                : "bg-emerald-50 text-emerald-700";
                                                    return (
                                                        <span key={`${c.class_code}-${i}`} className={`px-1.5 py-0.5 rounded text-xs font-mono ${tone}`} title={c.valid_to ? `${c.class_code} · valid to ${c.valid_to}` : c.class_code}>
                                                            <span className="font-semibold">{c.class_code}</span>
                                                            {c.valid_to ? <span className="ml-1">· {formatDate(c.valid_to)}</span> : null}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 font-mono text-xs">
                                        {d.hazardous_license ? (
                                            d.hazardous_license_expiry ? (() => {
                                                const hd = daysUntil(d.hazardous_license_expiry);
                                                const tone = hd == null ? "text-fleet-inkSoft" : hd < 0 ? "text-fleet-critical" : hd <= 30 ? "text-fleet-warning" : "text-emerald-700";
                                                return (
                                                    <span className={tone} title={hd != null ? (hd < 0 ? `expired ${-hd}d ago` : `in ${hd}d`) : ""}>
                                                        {formatDate(d.hazardous_license_expiry)}
                                                    </span>
                                                );
                                            })() : <span className="text-fleet-warning">HAZMAT · no date</span>
                                        ) : <span className="text-fleet-inkSoft">—</span>}
                                    </td>
                                    <td className="px-5 py-3 font-mono text-xs">{vehMap[d.vehicle_id]?.reg_number || "—"}</td>
                                    <td className="px-5 py-3"><Badge variant={d.status === "active" ? "healthy" : d.status === "suspended" ? "critical" : "warning"}>{d.status?.replace("_", " ")}</Badge></td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap no-print">
                                        <Button variant="secondary" size="sm" onClick={() => openRowDocs(d)} data-testid={`docs-driver-${d.id}`}><FileText size={14} /> Docs</Button>
                                        <Button variant="ghost" size="sm" onClick={() => edit(d)} data-testid={`edit-driver-${d.id}`}>Edit</Button>
                                        <Button variant="ghost" size="sm" onClick={() => del(d.id)} data-testid={`del-driver-${d.id}`}>Del</Button>
                                    </td>
                                </tr>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">
                                {list.length === 0 ? "No drivers yet." : "No drivers match your search."}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </Surface>

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit driver" : "Add driver"} size="lg"
                footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-driver">Save</Button></>}>
                {/* AI / Manual mode toggle — only when adding a new driver */}
                {!editing && (
                    <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
                        <div className="inline-flex rounded-md border border-fleet-border bg-white text-xs overflow-hidden" data-testid="driver-add-mode">
                            <button
                                onClick={() => setAddMode("ai")}
                                className={`px-3 py-1.5 flex items-center gap-1.5 ${addMode === "ai" ? "bg-fleet-ink text-white" : "text-fleet-inkSoft hover:text-fleet-ink"}`}
                                data-testid="driver-mode-ai"
                            >
                                <Sparkle size={12} /> AI Mode
                            </button>
                            <button
                                onClick={() => setAddMode("manual")}
                                className={`px-3 py-1.5 border-l border-fleet-border ${addMode === "manual" ? "bg-fleet-ink text-white" : "text-fleet-inkSoft hover:text-fleet-ink"}`}
                                data-testid="driver-mode-manual"
                            >
                                Manual Mode
                            </button>
                        </div>
                        <span className="text-xs text-fleet-inkSoft">
                            {addMode === "ai"
                                ? "Upload one or more licence pages — AI fills the fields below."
                                : "Type every field by hand."}
                        </span>
                    </div>
                )}

                {/* AI extraction panel */}
                {!editing && addMode === "ai" && (
                    <div className="mb-4 p-4 border border-dashed border-fleet-border rounded-md bg-slate-50/40">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkle size={14} className="text-fleet-accent" />
                            <span className="text-sm font-medium">Licence AI extraction</span>
                            <span className="text-xs text-fleet-inkSoft">Claude Sonnet 4.5 vision · multi-page</span>
                        </div>
                        <input
                            type="file"
                            accept="image/*,.pdf"
                            multiple
                            onChange={(e) => { setAiFiles(Array.from(e.target.files || [])); setAiExtracted(null); }}
                            className="block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-fleet-border file:bg-white file:text-fleet-ink file:cursor-pointer"
                            data-testid="driver-ai-files"
                        />
                        {aiFiles.length > 0 && (
                            <div className="text-xs text-fleet-inkSoft mt-2">
                                {aiFiles.length} file(s) selected · {aiFiles.map((f) => f.name).join(", ")}
                            </div>
                        )}
                        <div className="mt-3 flex items-center gap-3 flex-wrap">
                            <Button
                                onClick={runAiExtract}
                                disabled={aiFiles.length === 0 || aiBusy}
                                data-testid="driver-ai-extract"
                            >
                                <Sparkle size={14} /> {aiBusy ? "Extracting…" : "Extract with AI"}
                            </Button>
                            {aiExtracted && (
                                <span className="text-xs text-emerald-700 flex items-center gap-1">
                                    <CheckCircle size={12} /> Fields pre-filled — please verify before saving.
                                </span>
                            )}
                        </div>
                        {aiExtracted && (
                            <div className="mt-3 space-y-2 text-xs">
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {[
                                        ["NAME", aiExtracted.name],
                                        ["DL #", aiExtracted.license_number],
                                        ["ISSUED BY", aiExtracted.rto_authority],
                                        ["ISSUE DATE", aiExtracted.issue_date],
                                        ["HAZMAT", aiExtracted.hazardous_license ? "YES" : "NO"],
                                        ["HAZMAT EXPIRY", aiExtracted.hazardous_license_expiry],
                                    ].map(([k, v]) => (
                                        <div key={k} className="px-2 py-1.5 bg-white border border-fleet-border rounded">
                                            <div className="label-mono">{k}</div>
                                            <div className="font-mono truncate" title={v || ""}>{v || "—"}</div>
                                        </div>
                                    ))}
                                </div>
                                {aiExtracted.cov_classes && aiExtracted.cov_classes.length > 0 && (
                                    <div className="text-fleet-inkSoft">
                                        Extracted {aiExtracted.cov_classes.length} COV class(es) — edit them in the table below.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Manual mode — attach licence pages to upload after save */}
                {!editing && addMode === "manual" && (
                    <div className="mb-4 p-4 border border-dashed border-fleet-border rounded-md">
                        <div className="flex items-center gap-2 mb-2">
                            <UploadSimple size={14} className="text-fleet-inkSoft" />
                            <span className="text-sm font-medium">Licence pages (optional)</span>
                        </div>
                        <input
                            type="file"
                            accept="image/*,.pdf"
                            multiple
                            onChange={(e) => setManualFiles(Array.from(e.target.files || []))}
                            className="block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-fleet-border file:bg-white file:text-fleet-ink file:cursor-pointer"
                            data-testid="driver-manual-files"
                        />
                        {manualFiles.length > 0 ? (
                            <div className="text-xs text-fleet-inkSoft mt-2">
                                {manualFiles.length} file(s) selected · {manualFiles.map((f) => f.name).join(", ")}
                            </div>
                        ) : (
                            <div className="text-xs text-fleet-inkSoft mt-2">
                                You can attach the scanned licence pages here. They'll be uploaded once the driver is saved
                                and become viewable from the <strong>Licence</strong> button on the driver row.
                            </div>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                    <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <Input label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    <Input label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    <Input label="License #" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value.toUpperCase() })} />
                    <Select label="License class" value={form.license_class} onChange={(e) => setForm({ ...form, license_class: e.target.value })} options={CLASSES} />
                    <Input label="License expiry" type="date" value={form.license_expiry} onChange={(e) => setForm({ ...form, license_expiry: e.target.value })} />
                    {form.license_expiry && (
                        <div className="col-span-2 -mt-2 text-xs">
                            {(() => {
                                const d = daysUntil(form.license_expiry);
                                if (d == null) return null;
                                if (d < 0) return <span className="text-fleet-critical font-mono">DL expired {-d} day(s) ago</span>;
                                if (d <= 30) return <span className="text-fleet-warning font-mono">DL expires in {d} day(s)</span>;
                                return <span className="text-fleet-inkSoft font-mono">DL valid for {d} more day(s)</span>;
                            })()}
                        </div>
                    )}
                    <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={["active", "inactive", "suspended", "on_leave"]} />
                    <VehicleSearchSelect
                        vehicles={vehicles}
                        value={form.vehicle_id}
                        onChange={(id) => setForm({ ...form, vehicle_id: id })}
                        label="Assigned vehicle"
                        placeholder="Search by reg # (or leave blank)…"
                        allowClear
                        testid="driver-vehicle-search"
                    />
                    <Input label="Notes" className="col-span-2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>

                {/* Editable Class of Vehicle (COV) list — works for Add & Edit */}
                <div className="mt-5">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <div className="label-mono">CLASS OF VEHICLE (COV) · {(form.cov_classes || []).length}</div>
                            <div className="text-xs text-fleet-inkSoft">Add every class endorsed on the licence with its own validity dates.</div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setForm({ ...form, cov_classes: [...(form.cov_classes || []), { _uid: _newCovUid(), class_code: "LMV", valid_from: "", valid_to: "" }] })}
                            className="text-xs text-fleet-accent hover:underline font-medium"
                            data-testid="cov-add-row"
                        >
                            + Add class
                        </button>
                    </div>
                    {(form.cov_classes || []).length === 0 ? (
                        <div className="text-xs text-fleet-inkSoft border border-dashed border-fleet-border rounded-md p-3 text-center">
                            No classes added yet. Click <strong>+ Add class</strong> to add one.
                        </div>
                    ) : (
                        <div className="border border-fleet-border rounded-md overflow-hidden">
                            <table className="w-full text-xs">
                                <thead><tr className="bg-slate-50 text-left">
                                    <th className="px-3 py-2 font-normal label-mono">CLASS</th>
                                    <th className="px-3 py-2 font-normal label-mono">VALID FROM</th>
                                    <th className="px-3 py-2 font-normal label-mono">VALID TO</th>
                                    <th className="px-3 py-2"></th>
                                </tr></thead>
                                <tbody>
                                    {(form.cov_classes || []).map((c, i) => {
                                        const update = (patch) => {
                                            const next = [...form.cov_classes];
                                            next[i] = { ...next[i], ...patch };
                                            setForm({ ...form, cov_classes: next });
                                        };
                                        const remove = () => {
                                            const next = form.cov_classes.filter((_, j) => j !== i);
                                            setForm({ ...form, cov_classes: next });
                                        };
                                        return (
                                            <tr key={c._uid || `cov-row-${i}`} className="border-t border-fleet-border">
                                                <td className="px-3 py-1.5">
                                                    <select
                                                        value={c.class_code || "LMV"}
                                                        onChange={(e) => update({ class_code: e.target.value })}
                                                        className="w-full px-2 py-1 border border-fleet-border rounded text-xs font-mono bg-white"
                                                        data-testid={`cov-class-${i}`}
                                                    >
                                                        {CLASSES.map((cl) => <option key={cl} value={cl}>{cl}</option>)}
                                                        {c.class_code && !CLASSES.includes(c.class_code) && (
                                                            <option key={c.class_code} value={c.class_code}>{c.class_code}</option>
                                                        )}
                                                    </select>
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    <input type="date" value={c.valid_from || ""}
                                                        onChange={(e) => update({ valid_from: e.target.value })}
                                                        className="w-full px-2 py-1 border border-fleet-border rounded text-xs font-mono"
                                                        data-testid={`cov-from-${i}`} />
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    <input type="date" value={c.valid_to || ""}
                                                        onChange={(e) => update({ valid_to: e.target.value })}
                                                        className="w-full px-2 py-1 border border-fleet-border rounded text-xs font-mono"
                                                        data-testid={`cov-to-${i}`} />
                                                </td>
                                                <td className="px-3 py-1.5 text-right">
                                                    <button type="button" onClick={remove}
                                                        className="text-xs text-fleet-critical hover:underline inline-flex items-center gap-1"
                                                        data-testid={`cov-remove-${i}`}>
                                                        <Trash size={12} /> Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Hazardous license section */}
                <div className="mt-5 p-4 border border-fleet-border rounded-md bg-slate-50/60">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox"
                            checked={!!form.hazardous_license}
                            onChange={(e) => setForm({ ...form, hazardous_license: e.target.checked, hazardous_license_expiry: e.target.checked ? form.hazardous_license_expiry : "" })}
                            className="h-4 w-4 rounded border-fleet-border"
                            data-testid="hazardous-license-check"
                        />
                        <span className="text-sm font-medium">Hazardous materials license (HAZMAT)</span>
                    </label>
                    {form.hazardous_license && (
                        <div className="mt-3 max-w-sm">
                            <Input label="Hazardous license expiry" type="date"
                                value={form.hazardous_license_expiry}
                                onChange={(e) => setForm({ ...form, hazardous_license_expiry: e.target.value })}
                                data-testid="hazardous-license-expiry" />
                            {form.hazardous_license_expiry && (() => {
                                const d = daysUntil(form.hazardous_license_expiry);
                                if (d == null) return null;
                                if (d < 0) return <div className="text-xs text-fleet-critical font-mono mt-1.5">HAZMAT expired {-d} day(s) ago</div>;
                                if (d <= 30) return <div className="text-xs text-fleet-warning font-mono mt-1.5">HAZMAT expires in {d} day(s)</div>;
                                return <div className="text-xs text-fleet-inkSoft font-mono mt-1.5">HAZMAT valid for {d} more day(s)</div>;
                            })()}
                        </div>
                    )}
                </div>
                <div className="mt-3 text-xs text-fleet-inkSoft flex items-center gap-1.5">
                    <Sparkle size={12} /> Tip: After saving, upload the licence scan below so the file is retained for audits.
                </div>

                {/* Licence documents — visible in Edit mode only */}
                {editing && (
                    <div className="mt-5 p-4 border border-fleet-border rounded-md">
                        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                            <div>
                                <div className="text-sm font-medium flex items-center gap-2">
                                    <FileText size={14} className="text-fleet-inkSoft" /> Licence documents · {docs.length}
                                </div>
                                <div className="text-xs text-fleet-inkSoft">View, replace or delete the uploaded licence files for this driver.</div>
                            </div>
                            <label className="cursor-pointer inline-block">
                                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLicense(f); e.target.value = ""; }}
                                    data-testid="edit-upload-license-input" />
                                <Button as="span" variant="secondary" size="sm"><UploadSimple size={14} /> Upload licence</Button>
                            </label>
                        </div>
                        {docs.length === 0 ? (
                            <div className="text-center py-6 text-xs text-fleet-inkSoft border border-dashed border-fleet-border rounded-md">No licence file uploaded yet.</div>
                        ) : (
                            <div className="space-y-2">
                                {docs.map((d) => (
                                    <div key={`${d.kind}-${d.id}`} className="border border-fleet-border rounded-md p-3 flex items-center justify-between gap-3 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Sparkle size={12} className="text-fleet-accent" />
                                                <div className="text-sm font-medium truncate">{d.original_filename}</div>
                                                {d.kind === "compliance" ? (
                                                    <Badge variant={d.ai_status === "ok" ? "healthy" : "warning"}>{d.ai_status === "ok" ? "AI OK" : "AI partial"}</Badge>
                                                ) : (
                                                    <Badge variant="low">Attachment</Badge>
                                                )}
                                            </div>
                                            <div className="mt-1 text-xs text-fleet-inkSoft font-mono">
                                                {d.kind === "compliance"
                                                    ? `${d.document_number || "—"} · expiry ${formatDate(d.expiry_date) || "—"}`
                                                    : "Uploaded during driver add — view or delete the original file."}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer"
                                                className="text-xs text-fleet-accent hover:underline inline-flex items-center gap-1"
                                                data-testid={`doc-view-${d.id}`}>
                                                <FileText size={12} /> View
                                            </a>
                                            <button onClick={() => deleteDoc(d)}
                                                className="text-xs text-fleet-critical hover:underline inline-flex items-center gap-1"
                                                data-testid={`doc-del-${d.id}`}>
                                                <Trash size={12} /> Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* Row-level licence documents quick-view */}
            <Modal open={!!docsFor} onClose={() => { setDocsFor(null); setRowDocs([]); }} size="lg"
                title={docsFor ? `Licence documents · ${docsFor.name}` : ""}>
                {docsFor && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="text-xs text-fleet-inkSoft">
                                {rowDocs.length} file(s) on record for this driver.
                            </div>
                            <label className="cursor-pointer inline-block">
                                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadRowDoc(f); e.target.value = ""; }}
                                    data-testid="row-upload-license-input" />
                                <Button as="span"><UploadSimple size={14} /> Upload licence</Button>
                            </label>
                        </div>
                        {rowDocs.length === 0 ? (
                            <div className="text-center py-10 text-sm text-fleet-inkSoft border border-dashed border-fleet-border rounded-md">
                                No licence file uploaded yet.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {rowDocs.map((d) => (
                                    <div key={`${d.kind}-${d.id}`} className="border border-fleet-border rounded-md p-3 flex items-center justify-between gap-3 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Sparkle size={12} className="text-fleet-accent" />
                                                <div className="text-sm font-medium truncate">{d.original_filename}</div>
                                                {d.kind === "compliance" ? (
                                                    <Badge variant={d.ai_status === "ok" ? "healthy" : "warning"}>{d.ai_status === "ok" ? "AI OK" : "AI partial"}</Badge>
                                                ) : (
                                                    <Badge variant="low">Attachment</Badge>
                                                )}
                                            </div>
                                            <div className="mt-1 text-xs text-fleet-inkSoft font-mono">
                                                {d.kind === "compliance"
                                                    ? `${d.document_number || "—"} · expiry ${formatDate(d.expiry_date) || "—"}`
                                                    : "Uploaded during driver add — view or delete the original file."}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer"
                                                className="text-xs text-fleet-accent hover:underline inline-flex items-center gap-1"
                                                data-testid={`row-doc-view-${d.id}`}>
                                                <FileText size={12} /> View
                                            </a>
                                            <button onClick={() => deleteRowDoc(d)}
                                                className="text-xs text-fleet-critical hover:underline inline-flex items-center gap-1"
                                                data-testid={`row-doc-del-${d.id}`}>
                                                <Trash size={12} /> Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}
